import { Modal } from '@mantine/core';
import useCreatePlace from '../../../hooks/places/useCreatePlace.ts';
import MutatePlaceForm, {
  MutatePlaceFormValues,
} from '../../ui/places/MutatePlaceForm.tsx';
import { CreatePlaceRequest } from '../../../api/requests/place-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { useMutation } from '@tanstack/react-query';
import { ErrorResponse } from '../../../api/responses/error-response.ts';
import { parseGoogleStreetViewUrl } from '../../../utils/googleStreetViewUrl.ts';

interface AddPlaceModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: (placeId: string) => void;
}

const AddPlaceModal = ({ opened, onClose, onSuccess }: AddPlaceModalProps) => {
  const { mutateAsync: createPlace } = useCreatePlace();

  const { mutate: handleSubmit, isPending: isCreatePlacePending } = useMutation<
    { id: string },
    ErrorResponse,
    MutatePlaceFormValues
  >({
    mutationFn: async (formValues) => {
      const { lat, lng, heading, pitch, fov } = parseGoogleStreetViewUrl(
        formValues.googleMapStreetViewUrl,
      );
      const createPlaceRequest: CreatePlaceRequest = {
        name: formValues.name,
        lat: lat,
        lng: lng,
        address: formValues.address,
        googleStreetViewURL: formValues.googleMapStreetViewUrl,
        googleStreetViewLat: lat,
        googleStreetViewLng: lng,
        googleStreetViewHeading: heading,
        googleStreetViewPitch: pitch,
        googleStreetViewFov: fov,
        notes: formValues.notes,
      };

      return await createPlace(createPlaceRequest);
    },
    onSuccess: (data) => {
      onSuccess?.(data.id);
    },
    onError: (error) => {
      if (error.code === 'INVALID_INPUT') {
        notifyError('新增地點失敗', '請檢查輸入內容是否有誤');
      } else if (error.code === 'PLACE_NAME_ALREADY_EXISTS') {
        notifyError('新增地點失敗', '地點名稱已存在');
      } else {
        notifyError('新增地點失敗', error.message);
      }
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="新增圖資"
      centered
      closeOnClickOutside={false}
      styles={{ title: { fontWeight: 700, fontSize: 16 } }}
    >
      <MutatePlaceForm
        initialValues={{
          name: '',
          address: '',
          googleMapStreetViewUrl: '',
          notes: '',
        }}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isSubmitting={isCreatePlacePending}
        submitButtonText={'新增'}
      />
    </Modal>
  );
};

export { AddPlaceModal };
